import { Injectable } from '@angular/core';
import { ComponentType } from '@angular/cdk/portal';
import { BehaviorSubject } from 'rxjs';

import { LocalStorageService } from '../../services/storage/local-storage.service';
import { RouterBaseService } from '../../services/router/router-base.service';
import { UtilityService } from '../../utils/utility.service';

export interface FinePrintContentComponent {
  id?: string;
  content: ComponentType<unknown>;
  contentData?: unknown;
}

export type FinePrintIdOrComponent = string | FinePrintContentComponent;

/**
 * An interface defining what the fine print object should contain
 */
export interface FinePrints {
  links?: Array<FinePrintIdOrComponent>;
  footnotes?: Array<FinePrintIdOrComponent>;
  tips?: Array<FinePrintIdOrComponent>;
  pagenotes?: Array<FinePrintIdOrComponent>;
  disclaimers?: Array<FinePrintIdOrComponent>;
  customs?: Array<FinePrintIdOrComponent>;
}

export interface WithFinePrintReferences {
  referencedFootnotes: Array<string>;
}

/**
 * This Service is used to pass the fine print data to the component from any other services who set it
 */
@Injectable({
  providedIn: 'root'
})
export class FinePrintService {
  /**
   * A behaviour subject used by the component to listen for fine print changes
   */
  readonly onFinePrintUpdate: BehaviorSubject<FinePrints>;

  /**
   * The fine prints object that contains all the relevant data for the component
   */
  private finePrints: FinePrints;

  /**
   * The current state's name/id
   */
  private stateId: string;

  /**
   * The last updated date
   */
  private lastUpdated: Date;

  private namespace: string;

  /**
   * The constructor of the service. Used to call resetFinePrints to set up the finePrints property and also
   * set up the subject and observable.
   *
   * @param localStorageService This is used for storing whether the user has fine print open in different states
   * @param routerService This is used for retrieving the current state name used for a unique storage id
   */
  constructor(private localStorageService: LocalStorageService, private routerService: RouterBaseService) {
    this.resetFinePrints();
    this.onFinePrintUpdate = new BehaviorSubject<FinePrints>(this.finePrints);
  }

  /**
   * Returns the current fine prints stored by the service
   *
   * @returns The fine prints property stored by the service
   */
  getFinePrints(): FinePrints {
    return this.finePrints;
  }

  /**
   * This function is called by other services after retrieving the fine print related to the page they are on
   *
   * @param finePrints The fine print data
   * @param stateNameSuffix Any suffix they want to add to differentiate between different fine prints in the same state
   * @param reset existing fineprints will be reset instead of modifying
   */
  setFinePrints(finePrints: FinePrints | undefined, stateNameSuffix?: string, reset: boolean = true): void {
    if (reset) {
      this.resetFinePrints();
    }
    if (finePrints !== undefined) {
      UtilityService.deepExtend(this.finePrints, finePrints);
    }
    this.stateId = this.routerService.getCurrentStateName();
    if (stateNameSuffix !== undefined) {
      this.stateId = this.stateId + stateNameSuffix;
    }
    this.onFinePrintUpdate.next(this.finePrints);
  }

  /**
   * Resets the fine print and notifies the component of the change.
   */
  clearFinePrints(): void {
    this.resetFinePrints();
    this.onFinePrintUpdate.next(this.finePrints);
  }

  setNamespace(namespace: string): void {
    this.namespace = namespace;
  }

  getNamespace(): string {
    return this.namespace;
  }

  /**
   * Returns the current state of the fine print (e.g. collapsed or open).
   * If stateId is defined, it will attempt to load it from local storage
   *
   * @returns True for collapsed, false for open
   */
  getState(): boolean {
    let finePrintStateKey;
    let localState;
    if (this.stateId !== undefined) {
      finePrintStateKey = this.getFinePrintStateKey();
      localState = this.localStorageService.getItem(finePrintStateKey);

      if (localState === undefined || localState === null) {
        this.localStorageService.setItem(finePrintStateKey, 'false');
        return false;
      }
      return localState === 'true';
    }
    return false;
  }

  /**
   * Used for toggling the fine print between collapsed and open and returns the inverted stated.
   * Also saves the new value into the local storage.
   *
   * @returns The new value for the state of the fine print. True for collapsed, false for open.
   */
  invertState(): boolean {
    const finePrintStateKey = this.getFinePrintStateKey();
    const previousState = this.localStorageService.getItem(finePrintStateKey);
    const invertedState = previousState !== 'true';

    this.localStorageService.setItem(finePrintStateKey, invertedState);
    return invertedState;
  }

  /**
   * Returns the last updated date.
   *
   * @returns The lastUpdated property.
   */
  getLastUpdated(): Date {
    return this.lastUpdated;
  }

  /**
   * Returns the index of a particular referenced footnote based on an array of all referenced footnotes
   *
   * @param referencedFootnote The footnote that was referenced
   * @param referencedFootnotes An array of all the referenced footnotes
   * @returns The index of the referenced footnote or undefined if not found or referencedFootnotes not available
   */
  getFootnoteIndex(referencedFootnote: string, referencedFootnotes: Array<string>): number {
    if (!(referencedFootnotes instanceof Array) || referencedFootnotes.indexOf(referencedFootnote) >= 0) {
      if (this.finePrints !== undefined && this.finePrints.footnotes instanceof Array) {
        for (let i = 0; i < this.finePrints.footnotes.length; i++) {
          const item = this.finePrints.footnotes[i];
          const id = (typeof item === 'string') ? item : item.id;
          if (id === referencedFootnote) {
            return i + 1;
          }
        }
      }
    }
  }

  /**
   * appends or adds a footnote.
   *
   * @param footNote
   */
  appendFootNote(footNote: string): void {

    const updatedFinePrints: FinePrints = {};

    if (this.finePrints === undefined) {
      Object.assign(updatedFinePrints, { footnotes: [footNote] });
    } else {
      Object.assign(updatedFinePrints, this.finePrints);
      let footPrints = updatedFinePrints.footnotes;
      if (footPrints === undefined || footPrints.length === 0) {
        footPrints = [footNote];
        updatedFinePrints.footnotes = footPrints;
      } else if (!footPrints.some((aFootNote: FinePrintIdOrComponent): boolean => aFootNote === footNote)) {
        footPrints.push(footNote);
      }

    }

    this.setFinePrints(updatedFinePrints);
  }

  /**
   * removes the given footnote if exits
   *
   * @param footNote
   */
  removeFootNote(footNote: string): void {
    const updatedFinePrints: FinePrints = {};

    if (this.finePrints !== undefined) {
      Object.assign(updatedFinePrints, this.finePrints);
      let footPrints = updatedFinePrints.footnotes;
      if (footPrints !== undefined && footPrints.length > 0) {
        footPrints = footPrints.filter((aFootNote: FinePrintIdOrComponent ) => aFootNote !== footNote);
        updatedFinePrints.footnotes = footPrints;
        this.setFinePrints(updatedFinePrints);
      }

    }
  }

  /**
   * Used to reset the fine print back to its initial state
   */
  private resetFinePrints(): void {
    this.finePrints = {
      links: [],
      footnotes: [],
      tips: [],
      pagenotes: [],
      disclaimers: [],
      customs: []
    };
    this.stateId = undefined;
    this.lastUpdated = new Date();
  }

  /**
   * Returns the state key used for saving the fine print state to local storage
   *
   * @returns The state key
   */
  private getFinePrintStateKey(): string {
    return 'finePrintCollapsedState-' + this.stateId;
  }
}
